import React from 'react';
import { useAbility, PERM, type Ability, type Perm } from 'shared/access';
import { useLayout } from 'app/shell/LayoutProvider';
import { RackView } from 'features/rack-view';
import { SystemSlide } from 'features/system-slide';
import { SaveDialog, LoadDialog, ExportDialog } from 'features/save-load';
import { RepriceOverlay } from 'features/reprice';

export type OverlayDef = {
  id: string;
  title: string;
  Component: React.ComponentType;
  requires: Perm[];
  /** Renders its own Dialog/Viewer chrome (save, load, export, reprice) rather
   *  than being wrapped by the host's shared Viewer — see Overlays.tsx. */
  selfHosted?: boolean;
};

/* Both views are available to every tier — a locked user keeps System Slide and
 * Rack View (spec §2). Save/Load/Export/Reprice are self-hosted: they bring
 * their own Dialog/Viewer and are driven by the quote-actions context flags
 * rather than the layout service's single `overlay` slot.
 *
 * Nothing opens `systemSlide` since 2026-10-05: the sidebar's Export System
 * Slide exports the deck directly instead of showing this preview first. */
export const OVERLAYS: OverlayDef[] = [
  { id: 'rackView', title: 'Rack View', Component: RackView, requires: [] },
  { id: 'systemSlide', title: 'System Slide', Component: SystemSlide, requires: [] },
  { id: 'save', title: 'Save configuration', Component: SaveDialog, requires: [], selfHosted: true },
  { id: 'load', title: 'Load configuration', Component: LoadDialog, requires: [], selfHosted: true },
  { id: 'export', title: 'Export CSV', Component: ExportDialog, requires: [PERM.QUOTE_GENERATE], selfHosted: true },
  { id: 'reprice', title: 'Reprice against the current catalog', Component: RepriceOverlay, requires: [PERM.EXEC_CUSTOM_PRICING], selfHosted: true },
];

/** Extracted so the ability-gate itself is testable against a definition that
 * doesn't have to exist in the real OVERLAYS list (none of which is gated yet). */
export function isOverlayAllowed(def: OverlayDef, ability: Ability): boolean {
  return def.requires.every((p) => ability.can(p));
}

export function useActiveOverlay(): OverlayDef | null {
  const { overlay } = useLayout();
  const ability = useAbility();
  if (!overlay) return null;
  const def = OVERLAYS.find((o) => o.id === overlay);
  // A self-hosted overlay (save/load/export/reprice) is never rendered
  // from `active` — Overlays.tsx only wraps a NON-self-hosted def in the
  // shared Viewer. Resolving one here would leave layout.overlay set with no
  // visible UI and no spine control to clear it. Nothing calls
  // openOverlay('save'|'load'|'export'|'reprice') today, but returning null
  // for a self-hosted id makes that call a genuine no-op instead of a latent
  // half-open state.
  if (!def || def.selfHosted) return null;
  return isOverlayAllowed(def, ability) ? def : null;
}
